/**
 * Resolves the element a URL hash points at.
 *
 * Exact id first — that is what the browser does and what every well-formed
 * link needs. Only when nothing has that exact id do we look, inside the
 * article only, for an element whose id matches ignoring case, and only if
 * exactly one does. Related-source links arrive with anchors like `P3-5`
 * while the law's element is `p3-5` (8 of 12 such links in Årsberetningen
 * on 2026-09-17), and `getElementById` is case-sensitive, so those readers
 * landed at the top of the chapter instead of at the paragraph. Two ids that
 * differ only by case would be ambiguous; we then do nothing rather than
 * guess.
 */
export function findAnchorTarget(
    hash: string,
    exactScope: Document | Element,
    fallbackScope: Element | null = exactScope instanceof Element ? exactScope : null
): HTMLElement | null {
    if (!hash) return null;

    const exact =
        exactScope instanceof Document
            ? exactScope.getElementById(hash)
            : exactScope.querySelector<HTMLElement>(`#${CSS.escape(hash)}`);
    if (exact) return exact;

    if (!fallbackScope) return null;
    const wanted = hash.toLowerCase();
    const candidates = Array.from(
        fallbackScope.querySelectorAll<HTMLElement>("[id]")
    ).filter((el) => el.id.toLowerCase() === wanted);
    return candidates.length === 1 ? candidates[0] : null;
}
